import { Button, Modal } from "antd";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";

import type { DrawingPdf } from "@/utility/drawingPdf";

export default function PdfPreviewModal({
  file,
  open,
  onClose,
}: {
  file: DrawingPdf | null;
  open: boolean;
  onClose: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Modal
      open={open}
      onCancel={onClose}
      title={file?.name || t("pdfPreview.title") || "圖面預覽"}
      width="92%"
      style={{ maxWidth: 1100 }}
      centered
      zIndex={2000}
      destroyOnHidden
      styles={{
        content: {
          display: "flex",
          flexDirection: "column",
          maxHeight: "90vh",
          overflow: "hidden",
        },
        body: { padding: 0, flex: 1, minHeight: 0, overflow: "hidden" },
      }}
      footer={[
        <Button
          key="newTab"
          onClick={() => {
            if (file?.url)
              window.open(file.url, "_blank", "noopener,noreferrer");
          }}
        >
          {t("pdfPreview.openInNewTab") || "在新分頁開啟"}
        </Button>,
        <Button key="close" type="primary" onClick={onClose}>
          {t("pdfPreview.close") || "關閉"}
        </Button>,
      ]}
    >
      {file?.url ? (
        <iframe
          src={file.url}
          title={file.name || t("pdfPreview.title") || "圖面預覽"}
          style={{ height: "calc(90vh - 130px)", minHeight: 200 }}
          className="w-full rounded border border-gray-200"
        />
      ) : (
        <div className="p-6 text-sm text-gray-500">
          {t("pdfPreview.notFound") || "找不到對應的 PDF 圖面"}
        </div>
      )}
    </Modal>
  );
}

export function usePdfPreview() {
  const [file, setFile] = useState<DrawingPdf | null>(null);

  const openPdf = useCallback((next: DrawingPdf) => setFile(next), []);
  const closePdf = useCallback(() => setFile(null), []);

  return {
    pdfFile: file,
    openPdf,
    closePdf,
    pdfPreview: (
      <PdfPreviewModal file={file} open={!!file} onClose={closePdf} />
    ),
  };
}
